/**
 * iter 167 — Singleton SSE connection.
 *
 * Replaces the iter138 per-component useRealtimeEvents hook with a single
 * EventSource owned by the SseProvider mounted high in the tree (inside
 * AuthProvider). Pages still call `useRealtimeEvents(handlers)` and receive
 * exactly the same API — but under the hood multiple subscribers share ONE
 * EventSource connection. This:
 *   • Fixes the iter138 token-key bug (was reading the wrong localStorage
 *     key — SSE never connected after the AuthContext storage rename).
 *   • Eliminates duplicate /api/events/stream connections per navigation.
 *   • Keeps the realtime bus warm on the default /dashboard route (which
 *     previously had no useRealtimeEvents mount → no SSE at all).
 */
import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';

const SseContext = createContext(null);

const EVENT_TYPES = [
  'job.created', 'job.updated', 'job.assigned', 'job.deleted',
  'job.reassigned', 'job.tat_changed',
  'candidate.created', 'candidate.updated', 'candidate.status_changed', 'candidate.deleted',
  'client.created', 'client.updated', 'client.deleted',
  'notification.new',
  'audit.created',
  // iter 168 — team membership realtime updates
  'team.recruiter_added', 'team.recruiter_removed', 'team.updated',
  // iter 193/195 — B2C portal + MIS B2C Team live tables
  'b2c.developer.updated', 'b2c.interview.updated',
  'b2c.daily_update.updated', 'b2c.user.updated',
  // iter 194 — MIS teams live updates
  'mis.team.updated',
  // iter 202/205 — Turing workflow + Internal Screening
  'turing.updated', 'screening.updated', 'lead.updated',
  // iter 211 — async background import progress
  'import.updated',
  // iter 212 — persistent task queue progress (resume parse / smart match)
  'job.updated',
  // iter239 — page-access grant/revoke propagation (Turing Team sidebar)
  'access.updated',
  // iter269 — admin custom pipeline status catalogue changes
  'pipeline.statuses.updated',
];

export function SseProvider({ children }) {
  const { user, token } = useAuth();
  // Map<eventType, Set<handler>> — every subscriber registers their handler
  // here; dispatch picks the per-event Set on each frame.
  const subscribersRef = useRef(new Map());
  const esRef = useRef(null);

  useEffect(() => {
    if (!user?.id || !token) return undefined;
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return undefined;

    const url = `${backend}/api/events/stream?token=${encodeURIComponent(token)}`;
    let es;
    try {
      es = new EventSource(url, { withCredentials: false });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[SSE] EventSource unsupported:', e);
      return undefined;
    }
    esRef.current = es;

    const dispatch = (payload) => {
      const set = subscribersRef.current.get(payload?.type);
      if (!set || set.size === 0) return;
      set.forEach((fn) => {
        try { fn(payload); } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[SSE] handler error for', payload?.type, err);
        }
      });
    };

    const onEvent = (evt) => {
      try {
        dispatch(JSON.parse(evt.data));
      } catch {
        /* malformed frame — ignore */
      }
    };

    es.onmessage = onEvent;
    EVENT_TYPES.forEach((t) => es.addEventListener(t, onEvent));

    es.onerror = () => {
      // EventSource auto-reconnects on transient errors.
      if (es.readyState === EventSource.CLOSED) {
        // eslint-disable-next-line no-console
        console.warn('[SSE] connection permanently closed.');
      }
    };

    return () => {
      try { es.close(); } catch { /* no-op */ }
      esRef.current = null;
    };
  }, [user?.id, token]);

  const value = useRef({
    subscribe(type, handler) {
      if (!type || typeof handler !== 'function') return () => {};
      let set = subscribersRef.current.get(type);
      if (!set) {
        set = new Set();
        subscribersRef.current.set(type, set);
      }
      set.add(handler);
      return () => {
        const s = subscribersRef.current.get(type);
        if (s) s.delete(handler);
      };
    },
  }).current;

  return <SseContext.Provider value={value}>{children}</SseContext.Provider>;
}

/**
 * Page-facing API — register a handler map. Returns a teardown that
 * unsubscribes every entry. Calling this from multiple components is safe;
 * they all multiplex onto the single EventSource owned by SseProvider.
 */
export function useSseSubscribe(handlers) {
  const ctx = useContext(SseContext);
  useEffect(() => {
    if (!ctx || !handlers) return undefined;
    const unsubs = [];
    Object.entries(handlers).forEach(([type, fn]) => {
      if (typeof fn === 'function') unsubs.push(ctx.subscribe(type, fn));
    });
    return () => { unsubs.forEach((u) => u()); };
    // We intentionally re-subscribe whenever the handler map identity
    // changes — pages typically pass a stable closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, handlers]);
}
